import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Application } from './types';

const REEVALUATION_QUESTIONNAIRE_URL =
  'https://koneprod.service-now.com/esc?id=sc_cat_item&table=sc_cat_item&sys_id=9b39579a836d5a1030a098747daad393&recordUrl=com.glideapp.servicecatalog_cat_item_view.do%3Fv%3D1&sysparm_id=9b39579a836d5a1030a098747daad393';
const REEVALUATION_QUESTIONNAIRE_LABEL =
  'Initial Questionnaire - Cybersecurity, Legal & Data Protection - Kone Employee center';
const NOT_PROVIDED = 'Not provided';

interface QuestionnaireResponse {
  ban: string;
  questionnaireId?: string | null;
  questionnaireUrl?: string | null;
  fields: Record<string, unknown>;
}

export interface InitialQuestionnaireViewProps {
  app: Application;
  onBack: () => void;
}

// The helpers below only format the fetched questionnaire fields, which are
// hidden for now (see the commented-out block in the component). They are
// commented out too because `tsc` rejects unused declarations (TS6133).
// Uncomment them together with that block to show the fields again.
//
// function formatFieldLabel(fieldName: string): string {
//   const label = fieldName
//     .replace(/^u_/, '')
//     .replace(/_/g, ' ')
//     .replace(/\s+/g, ' ')
//     .trim();
//
//   return label.replace(/\b\w/g, character => character.toUpperCase());
// }
//
// function isEmptyValue(value: unknown): boolean {
//   if (value === null || value === undefined) return true;
//   if (typeof value === 'string') return value.trim() === '';
//   if (Array.isArray(value)) return value.length === 0;
//   if (typeof value === 'object') {
//     return Object.keys(value as Record<string, unknown>).length === 0;
//   }
//   return false;
// }
//
// function formatFieldValue(value: unknown): string {
//   if (isEmptyValue(value)) return NOT_PROVIDED;
//   if (typeof value === 'boolean') return value ? 'Yes' : 'No';
//   if (Array.isArray(value)) {
//     return (
//       value
//         .map(item => formatFieldValue(item))
//         .filter(item => item !== NOT_PROVIDED)
//         .join(', ') || NOT_PROVIDED
//     );
//   }
//   if (typeof value === 'object') {
//     const objectValue = value as Record<string, unknown>;
//     if (!isEmptyValue(objectValue.display_value)) {
//       return formatFieldValue(objectValue.display_value);
//     }
//     if (!isEmptyValue(objectValue.value)) {
//       return formatFieldValue(objectValue.value);
//     }
//     return JSON.stringify(value);
//   }
//   return String(value);
// }

function sortQuestionnaireFields(
  entries: Array<[string, unknown]>,
): Array<[string, unknown]> {
  return [...entries].sort(([left], [right]) => {
    const leftIsTechnical = left.startsWith('sys_');
    const rightIsTechnical = right.startsWith('sys_');
    if (leftIsTechnical !== rightIsTechnical) {
      return leftIsTechnical ? 1 : -1;
    }
    return left.localeCompare(right, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });
}

/** Initial Questionnaire module view with the complete live ServiceNow record. */
export function InitialQuestionnaireView({
  app,
  onBack,
}: InitialQuestionnaireViewProps) {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [questionnaire, setQuestionnaire] =
    useState<QuestionnaireResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadQuestionnaire = async () => {
      const hasReference = Boolean(
        app.cyber_security_questionnaire || app.cyber_security_questionnaire_id,
      );
      if (!hasReference) {
        setQuestionnaire(null);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('business-applications');
        const response = await fetchApi.fetch(
          `${baseUrl}/applications/${encodeURIComponent(
            app.number,
          )}/questionnaire`,
        );
        if (!response.ok) {
          throw new Error(`Failed to load questionnaire (${response.status})`);
        }

        const data: QuestionnaireResponse = await response.json();
        if (!cancelled) {
          setQuestionnaire(data);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError('Could not load the initial questionnaire.');
          setQuestionnaire(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadQuestionnaire();
    return () => {
      cancelled = true;
    };
  }, [app, discoveryApi, fetchApi]);

  const hasImpactLevel =
    app.security_impact !== null &&
    app.security_impact !== undefined &&
    String(app.security_impact).trim() !== '';
  const hasQuestionnaire = Boolean(
    questionnaire?.questionnaireId ||
      app.cyber_security_questionnaire_id ||
      app.cyber_security_questionnaire,
  );
  const fieldEntries = sortQuestionnaireFields(
    Object.entries(questionnaire?.fields ?? {}),
  );
  // Used only by the hidden fields block below; uncomment with that block.
  // const regularFields = fieldEntries.filter(
  //   ([name]) => !name.startsWith('sys_'),
  // );
  // const technicalFields = fieldEntries.filter(([name]) =>
  //   name.startsWith('sys_'),
  // );

  return (
    <div className="card detail-view-card">
      <div className="detail-view-header">
        <button type="button" className="back-button" onClick={onBack}>
          <span aria-hidden="true">&#8592;</span> Back to Application Modules
        </button>
        <h4 className="detail-view-title">Initial Questionnaire</h4>
      </div>

      {hasQuestionnaire ? (
        <>
          <p className="questionnaire-summary">
            Questionnaire ID (
            {questionnaire?.questionnaireId ??
              app.cyber_security_questionnaire_id ??
              NOT_PROVIDED}
            )
          </p>

          {/* "Loading questionnaire..." message hidden for now.
          {loading && (
            <p className="questionnaire-status">Loading questionnaire...</p>
          )}
          */}
          {error && <p className="questionnaire-error">{error}</p>}

          {!loading && !error && fieldEntries.length === 0 && (
            <p className="questionnaire-status">
              No questionnaire fields returned.
            </p>
          )}

          {/* Fetched questionnaire content is hidden for now; only the
              questionnaire ID above is shown. Uncomment to show it again.

          {!loading && !error && regularFields.length > 0 && (
            <dl className="questionnaire-fields">
              {regularFields.map(([name, value]) => (
                <div className="questionnaire-field" key={name}>
                  <dt>{formatFieldLabel(name)}</dt>
                  <dd>{formatFieldValue(value)}</dd>
                </div>
              ))}
            </dl>
          )}

          {!loading && !error && technicalFields.length > 0 && (
            <details className="questionnaire-technical-fields">
              <summary>Technical metadata</summary>
              <dl className="questionnaire-fields">
                {technicalFields.map(([name, value]) => (
                  <div className="questionnaire-field" key={name}>
                    <dt>{formatFieldLabel(name)}</dt>
                    <dd>{formatFieldValue(value)}</dd>
                  </div>
                ))}
              </dl>
            </details>
          )}
          */}
        </>
      ) : (
        <p>No cybersecurity questionnaire available</p>
      )}

      {hasImpactLevel && !hasQuestionnaire && (
        <p>
          please fill out this form for re-evaluation{' '}
          <a
            href={REEVALUATION_QUESTIONNAIRE_URL}
            target="_blank"
            rel="noreferrer"
          >
            {REEVALUATION_QUESTIONNAIRE_LABEL}
          </a>
        </p>
      )}
    </div>
  );
}
